import { memo, useCallback, useMemo, useRef, useState } from "react";

import { useIsBelowMobile } from "@react/hooks/useBreakpoint";
import {
  type BentoBoxCardProps,
  type VideoSource,
} from "@root/tablescape/state/bentoBox";
import {
  formatFullyQualifiedUrl,
  isValidUrl,
} from "@root/tablescape/utilities";

import { BentoBoxCardView } from "./BentoBoxCard.view";

const BentoBoxCardBase = (props: BentoBoxCardProps) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isBelowMobile = useIsBelowMobile();

  const handleToggleVideo = useCallback(() => {
    if (videoRef?.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
        setIsVideoPlaying(false);
      } else {
        videoRef.current.play();
        setIsVideoPlaying(true);
      }
    }
  }, [isVideoPlaying, setIsVideoPlaying, videoRef]);

  // Helper function to process video sources from array format
  const processVideoSourcesArray = (
    sources: Array<{ mime_type: string; url: string }>
  ): VideoSource[] => {
    return sources.map((source) => ({
      mimeType: source.mime_type,
      url: source.url,
    }));
  };

  // Helper function to process legacy video URL fields
  const processLegacyVideoUrls = useCallback((): VideoSource[] => {
    const sources: VideoSource[] = [];
    if (props.videoWebm) {
      sources.push({ mimeType: "video/webm", url: props.videoWebm });
    }
    if (props.videoMp4) {
      sources.push({ mimeType: "video/mp4", url: props.videoMp4 });
    }
    return sources;
  }, [props.videoMp4, props.videoWebm]);

  const videoSources = useMemo<VideoSource[]>(() => {
    // Desktop video priority: videoDesktop → videoWebm/videoMp4
    if (props.videoDesktop && props.videoDesktop.length > 0) {
      return processVideoSourcesArray(props.videoDesktop);
    }
    return processLegacyVideoUrls();
  }, [props.videoDesktop, processLegacyVideoUrls]);

  const mobileVideoSources = useMemo<VideoSource[]>(() => {
    // Mobile video priority: videoMobile → videoDesktop → videoWebm/videoMp4
    if (props.videoMobile && props.videoMobile.length > 0) {
      return processVideoSourcesArray(props.videoMobile);
    }
    if (props.videoDesktop && props.videoDesktop.length > 0) {
      return processVideoSourcesArray(props.videoDesktop);
    }
    return processLegacyVideoUrls();
  }, [props.videoMobile, props.videoDesktop, processLegacyVideoUrls]);

  // Responsive media selection
  // Use mobile sources if on mobile AND mobile sources exist and are non-empty
  const currentVideoSources = useMemo(() => {
    if (isBelowMobile && mobileVideoSources && mobileVideoSources.length > 0) {
      return mobileVideoSources;
    }
    return videoSources;
  }, [isBelowMobile, mobileVideoSources, videoSources]);

  const currentImageUrl = useMemo(() => {
    return isBelowMobile && props.mobileImageUrl
      ? props.mobileImageUrl
      : props.imageUrl;
  }, [isBelowMobile, props.mobileImageUrl, props.imageUrl]);

  const currentImageAlt = useMemo(() => {
    if (isBelowMobile && props.mobileImageAlt) {
      return props.mobileImageAlt;
    }
    if (isBelowMobile && props.mobileImageUrl && props.titleText) {
      return props.titleText;
    }
    if (props.imageAlt) {
      return props.imageAlt;
    }
    return props.titleText;
  }, [
    isBelowMobile,
    props.mobileImageAlt,
    props.mobileImageUrl,
    props.imageAlt,
    props.titleText,
  ]);

  // Select appropriate video alt text based on current video sources
  const currentVideoAlt = useMemo(() => {
    if (isBelowMobile && props.videoMobileAlt) {
      return props.videoMobileAlt;
    }
    if (isBelowMobile && props.videoMobile && props.titleText) {
      return props.titleText;
    }
    if (props.videoDesktopAlt) {
      return props.videoDesktopAlt;
    }
    return props.titleText;
  }, [
    isBelowMobile,
    props.videoMobileAlt,
    props.videoMobile,
    props.videoDesktopAlt,
    props.titleText,
  ]);

  // Validate and format image URL securely
  const safeImageUrl = useMemo(() => {
    if (!currentImageUrl) {
      return undefined;
    }

    // Validate URL and check for dangerous protocols
    if (!isValidUrl(currentImageUrl)) {
      return undefined;
    }

    // Check for dangerous protocols
    const lowerUrl = currentImageUrl.toLowerCase().trim();
    if (
      lowerUrl.startsWith("javascript:") ||
      lowerUrl.startsWith("data:") ||
      lowerUrl.startsWith("vbscript:")
    ) {
      return undefined;
    }

    try {
      return formatFullyQualifiedUrl(currentImageUrl);
    } catch {
      return undefined;
    }
  }, [currentImageUrl]);

  return (
    <BentoBoxCardView
      {...props}
      btnLabel={props.btnLabel}
      handleToggleVideo={handleToggleVideo}
      {...(safeImageUrl && { imageUrl: safeImageUrl })}
      {...(currentImageAlt && { imageAlt: currentImageAlt })}
      {...(currentVideoAlt && { videoAlt: currentVideoAlt })}
      isBelowMobile={isBelowMobile}
      isVideoPlaying={isVideoPlaying}
      videoRef={videoRef}
      videoSources={currentVideoSources}
    />
  );
};

export const BentoBoxCard = memo(BentoBoxCardBase);
